"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
export default function Whyoemmanufacturersuseevermark() {
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: barRef,
    isVisible: barVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: signatureRef,
    isVisible: signatureVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const philosophyParagraphs = [{
    id: 1,
    text: "Precision is not a luxury. It is a fundamental requirement for industrial integrity — a refusal to let critical identification fail under the harsh conditions of modern manufacturing. Every tag we produce is a permanent record in an environment where clarity is safety.",
    highlight: "a fundamental requirement"
  }, {
    id: 2,
    text: "Durability is not an option. It is an engineering mandate. To utilize Evermark’s laser-etched solutions — to insist on longevity in an era of disposable components — is to demand something more reliable than standard labeling. It is to demand absolute permanence.",
    highlight: "absolute permanence"
  }, {
    id: 3,
    text: "The production floor is our proving ground. A high-stakes environment where downtime is costly, errors are dangerous, and legibility is paramount. We do not just manufacture tags. We provide the essential markers that keep complex systems running on their own terms.",
    highlight: "proving ground"
  }, {
    id: 4,
    text: "This is our unwavering commitment: to engineer without compromise, to manufacture with precision, and to serve only those who understand that industrial identification, in its highest form, is not a commodity to be replaced but a standard to be maintained.",
    highlight: "unwavering commitment"
  }];
  return <section data-section-id="4124" id="whyoemmanufacturersuseevermark" className="relative bg-background text-foreground min-h-screen py-[200px] px-6 md:px-12 lg:px-20 overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")"
    }} />

      <div className="relative mx-auto max-w-[1400px]">
        {}
        <div ref={labelRef} className={`flex justify-center mb-16 md:mb-24 transition-all duration-700 ease-out ${labelVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"}`}>
          <span className="font-mono text-[14px] tracking-[0.3em] uppercase" style={{
          color: "var(--primary)"
        }}>
            THE STANDARD &mdash; 01 / PHILOSOPHY
          </span>
        </div>

        {}
        <h2 ref={headlineRef} className={`font-default font-bold uppercase leading-[0.92] tracking-[-0.04em] text-left mb-20 md:mb-28 transition-all duration-1000 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
        fontSize: "clamp(48px, 11vw, 180px)",
        width: "85%"
      }}>
          <span className="block text-foreground">WE BELIEVE</span>
          <span className="block text-foreground">PRECISION IS</span>
          <span className="block" style={{
          color: "var(--primary)"
        }}>
            A NECESSITY.
          </span>
          <span className="block" style={{
          color: "var(--primary)"
        }}>
            NOT AN OPTION.
          </span>
        </h2>

        {}
        <div ref={barRef} className={`mb-20 md:mb-28 transition-all duration-700 ease-out ${barVisible ? "opacity-100 scale-y-100" : "opacity-0 scale-y-0"}`} style={{
        transformOrigin: "top"
      }}>
          <div className="w-[4px] h-[80px]" style={{
          backgroundColor: "var(--primary)"
        }} />
        </div>

        {}
        <div ref={bodyRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-24 md:mb-32 transition-all duration-1000 ease-out ${bodyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="lg:col-span-4 lg:col-start-1">
            <div className="lg:sticky lg:top-24">
              <h3 className="font-default font-bold uppercase text-foreground tracking-[-0.02em] leading-[1] text-[28px] md:text-[32px]">
                OUR
                <br />
                ENGINEERING
              </h3>
              <div className="mt-6 h-[2px] w-16" style={{
              backgroundColor: "var(--primary)"
            }} />
            </div>
          </div>

          {}
          <div className="lg:col-span-6 lg:col-start-6">
            <div className="space-y-0">
              {philosophyParagraphs.map((paragraph, i) => <div data-index={i} key={paragraph.id}>
                  <p className="font-default text-foreground leading-[1.55]" style={{
                fontSize: "clamp(18px, 1.4vw, 22px)"
              }}>
                    {paragraph.text.split(paragraph.highlight).map((part, idx, arr) => <span key={idx}>
                        {part}
                        {idx < arr.length - 1 && <span className="font-default italic" style={{
                    color: "var(--primary)"
                  }}>
                            {paragraph.highlight}
                          </span>}
                      </span>)}
                  </p>

                  {}
                  {i < philosophyParagraphs.length - 1 && <div className="flex py-8">
                      <div className="w-[8px] h-[8px]" style={{
                  backgroundColor: "var(--primary)"
                }} aria-hidden="true" />
                    </div>}
                </div>)}
            </div>
          </div>
        </div>

        {}
        <div ref={signatureRef} className={`mt-24 md:mt-32 transition-all duration-1000 ease-out ${signatureVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="font-mono uppercase tracking-[0.25em] mb-8" style={{
          fontSize: "12px",
          color: "var(--muted-foreground)"
        }}>
            CERTIFIED &mdash; EVERMARK ENGINEERING, USA, 2024
          </p>

          {}
          <div className="relative h-[1px] w-full overflow-hidden bg-border">
            <div className={`absolute inset-y-0 left-0 h-[1px] transition-transform duration-[1500ms] ease-out ${signatureVisible ? "scale-x-100" : "scale-x-0"}`} style={{
            backgroundColor: "var(--primary)",
            width: "100%",
            transformOrigin: "left",
            boxShadow: "0 0 12px var(--primary)"
          }} />
          </div>
        </div>
      </div>
    </section>;
}